<template>
  <section class="regional-winners" :class="{ desktop: $isDesktop }">
    <section
      v-for="(region, index) in data"
      :id="region.area"
      :key="index"
      class="regional-winner"
    >
      <div class="region-header">
        <div class="intro">
          <h2><span>Best in</span> {{ region.areaTitle }}</h2>
          <p>{{ region.intro }}</p>
        </div>
        <div class="map-wrapper">
          <uk-regions-map class="uk-regions-map" :area="region.area" />
        </div>
      </div>
      <div class="winners-list">
        <ul>
          <li v-for="(winner, count) in region.winners" :key="count">
            <a :href="winner.url" target="_blank">{{ winner.name }}</a>
          </li>
        </ul>
      </div>
    </section>
  </section>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import UkRegionsMap from '~/apps/maps/components/UkRegionsMap.vue'

interface RegionalWinnersData {
  areaTitle: string
  area: string
  intro: string
  winners: [
    {
      name: string
      url: string
    },
  ]
}

@Component({
  components: {
    UkRegionsMap,
  },
})
class RegionalWinners extends Vue {
  @Prop()
  data: RegionalWinnersData[]
}

export default toNative(RegionalWinners)
</script>

<style scoped>
p {
  font-size: 0.8em;
  max-width: 50ch;
  line-height: 1.4;
  text-shadow: 1px 1px 2px #ffffff;
}

.regional-winner {
  margin: 0;
  padding: 0;
}

.region-header {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  overflow: clip;
  position: relative;
  min-height: 17em;
}

.intro {
  padding: 3em 0 2em;
  z-index: 1;
  pointer-events: none;
}

.regional-winner:nth-child(even) {
  background-color: var(--colour-page-background-light);
  box-shadow: 0 0 0 100vw var(--colour-page-background-light);
  clip-path: inset(0 -100vw);
}

.regional-winner h2 {
  text-shadow: 1px 1px 11px #ffffff;
}

.regional-winner:nth-child(even) h2 {
  text-shadow: 1px 1px 11px var(--colour-page-background-light);
}

.regional-winner:nth-child(even) p {
  text-shadow: 1px 1px 2px var(--colour-page-background-light);
}

h2 span {
  font-weight: normal;
  display: block;
}

.map-wrapper {
  overflow: clip;
  width: 65vw;
  position: absolute;
  right: -0.5em;
  top: -57vw;
  bottom: 0;
  isolation: isolate;
}

.map-wrapper:before {
  content: '';
  background: linear-gradient(0deg, #fff, #fff0);
  width: 100%;
  height: 4em;
  bottom: 0;
  position: absolute;
  pointer-events: none;
}

.regional-winner:nth-child(even) .map-wrapper:before {
  background: linear-gradient(
    0deg,
    var(--colour-page-background-light),
    #e1e5ea00
  );
}

.regional-winner#north-wales .map-wrapper {
  top: -50vw;
}

.regional-winner#south-wales .map-wrapper {
  top: -63vw;
}

.regional-winner#north-west .map-wrapper {
  top: -42vw;
}

.regional-winner#north-east .map-wrapper {
  top: -36vw;
}

.regional-winner#south-west .map-wrapper {
  top: -63vw;
}

.regional-winner#south-east .map-wrapper {
  top: -63vw;
}

.regional-winner#central-england .map-wrapper {
  top: -55vw;
}

.regional-winner#scotland .map-wrapper {
  top: -26vw;
}

.uk-regions-map {
  width: 100%;
  height: auto;
}

.winners-list {
  padding: 0 0 2em;
}

.regional-winners ul {
  display: grid;
  gap: 0.75em;
  grid-template-columns: repeat(auto-fit, minmax(18em, 1fr));
  grid-auto-rows: auto;
  align-items: center;
  counter-reset: winner 0;
}

.regional-winners ul li a:before {
  counter-increment: winner;
  content: counter(winner);
  position: absolute;
  left: 1em;
  top: auto;
  font-weight: normal;
  font-size: var(--small);
}

.regional-winners a {
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--colour-page-background-light);
  border-radius: 0.25em;
  color: var(--colour-dark-blue);
  min-height: 2em;
  transition:
    box-shadow 0.15s ease-out,
    transform 0.25s ease-out;
  padding: 0.3em 2em;
  line-height: 1.1;
  text-align: center;
  font-weight: bold;
  position: relative;
  font-size: var(--body);
}

.regional-winner:nth-child(even) .winners-list a {
  background-color: white;
}

/* desktop & tablet */
.desktop.regional-winners p {
  font-size: 1em;
  line-height: 1.6;
}

.desktop .region-header {
  min-height: 22em;
}

.desktop .regional-winners ul {
  gap: 1em;
}

.desktop .regional-winners a {
  min-height: 3em;
  font-size: inherit;
}

.desktop.regional-winners a:hover,
.desktop.regional-winners a:focus-visible {
  transform: scale(1.05);
  outline: none;
  background-color: var(--colour-dark-blue);
  color: white;
}

.desktop.regional-winners .regional-winner {
  margin: 6em 0;
  border-top: 1px solid #93a0ae;
}

.desktop.regional-winners .regional-winner:nth-child(even) {
  border-top: none;
}

/* > 1600 */
@media only screen and (min-width: 1600px) {
  .map-wrapper {
    width: 65em;
    top: -57em;
  }

  .regional-winner#north-wales .map-wrapper {
    top: -50em;
  }

  .regional-winner#south-wales .map-wrapper {
    top: -63em;
  }

  .regional-winner#north-west .map-wrapper {
    top: -42em;
  }

  .regional-winner#north-east .map-wrapper {
    top: -36em;
  }

  .regional-winner#south-west .map-wrapper {
    top: -63em;
  }

  .regional-winner#south-east .map-wrapper {
    top: -63em;
  }

  .regional-winner#central-england .map-wrapper {
    top: -55em;
  }

  .regional-winner#scotland .map-wrapper {
    top: -26em;
  }
}
</style>
